// AddEventSection — Presentational "Add Events" quick-actions panel for the
// EditTripModal: the gray box headed by the Plus icon + "Add Events" title,
// followed by the row of action buttons — "Upload PDF", "Add Flight",
// "Add Hotel", "Add Car Rental", "Add Dining", and "Add Meeting".
//
// Originally extracted verbatim from `EditTripModal.js` (the "Quick Actions"
// block) as part of the behavior-preserving refactor (issue #240). "Add
// Meeting" was folded in from the standalone "Business Meetings" section so
// all six manual-add actions live in one consistent row instead of one of
// them getting its own promoted widget. The Upload PDF button calls
// `setShowPDFUpdater(true)`, the Flight/Hotel/Car/Dining buttons call
// `handleManualAddEvent('<type>')`, and Add Meeting calls
// `setShowAddMeeting(true)`; here those are surfaced as the `onUploadPDF`,
// `onAddEvent(eventType)`, and `onAddMeeting()` callbacks so the component
// stays render-only. Wrapped in React.memo. Button labels, colors, the
// `data-testid="add-dining-btn"` hook, `title` tooltips, the inline hover
// handlers, icons (lucide-react), and styles are identical to the source
// (Add Meeting reuses the Business Meetings section's original purple
// `#7c3aed` accent and `Building2` icon).
//
// Props:
//   onUploadPDF  - () => void invoked by the "Upload PDF" button
//   onAddEvent   - (eventType) => void invoked by the Add Flight/Hotel/Car/Dining
//                  buttons with 'flight' | 'hotel' | 'car' | 'dining'
//   onAddMeeting - () => void invoked by the "Add Meeting" button
//
// Validates: Requirements 3.3, 3.4, 1.2, 1.6

import React from 'react';
import { Plus, Upload, Car, Building, Building2, Plane, Utensils } from 'lucide-react';

export const AddEventSection = React.memo(function AddEventSection({
  onUploadPDF,
  onAddEvent,
  onAddMeeting
}) {
  return (
    <div style={{
      marginBottom: '24px',
      padding: '16px',
      backgroundColor: '#f8fafc',
      borderRadius: '8px',
      border: '1px solid #e2e8f0'
    }}>
      <h3 style={{
        fontSize: '16px',
        fontWeight: '600',
        color: '#1f2937',
        marginBottom: '12px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <Plus size={16} style={{ color: '#059669' }} />
        Add Events
      </h3>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        <button
          onClick={() => onUploadPDF()}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 12px',
            backgroundColor: '#059669',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#047857'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#059669'}
          title="Upload PDF to detect car rentals, hotels, or flights"
        >
          <Upload size={14} />
          Upload PDF
        </button>
        <button
          onClick={() => onAddEvent('flight')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 12px',
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
          title="Manually add a flight"
        >
          <Plane size={14} />
          Add Flight
        </button>
        <button
          onClick={() => onAddEvent('hotel')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 12px',
            backgroundColor: '#1e40af',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#1d4ed8'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#1e40af'}
          title="Manually add a hotel"
        >
          <Building size={14} />
          Add Hotel
        </button>
        <button
          onClick={() => onAddEvent('car')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 12px',
            backgroundColor: '#dc2626',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#b91c1c'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#dc2626'}
          title="Manually add a car rental"
        >
          <Car size={14} />
          Add Car Rental
        </button>
        <button
          data-testid="add-dining-btn"
          onClick={() => onAddEvent('dining')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 12px',
            backgroundColor: '#d97706',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#b45309'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#d97706'}
          title="Manually add a dining reservation"
        >
          <Utensils size={14} />
          Add Dining
        </button>
        <button
          onClick={() => onAddMeeting()}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 12px',
            backgroundColor: '#7c3aed',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#6d28d9'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#7c3aed'}
          title="Manually add a business meeting"
        >
          <Building2 size={14} />
          Add Meeting
        </button>
      </div>
    </div>
  );
});

export default AddEventSection;
